import { useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonToolbar,
  Col,
  Container,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import TimelapseIcon from "@material-ui/icons/Timelapse";
import BlockIcon from "@material-ui/icons/Block";
import StorefrontIcon from "@material-ui/icons/Storefront";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import moment from "moment";
import { useHistory, useLocation } from "react-router";
import { useSellerReviewQueue } from "../../hooks/useSellerReviewQueue.js";
import { useDispatch, useSelector } from "react-redux";

import {
  getSellers,
  getSellerDetail,
  softDeleteSeller,
  deleteSeller,
  reactivateSeller,
} from "../../actions/vendorActions";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import AppSelect from "../../components/ui/AppSelect";

const Sellers = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSoftDeleteModal, setShowSoftDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [currentSellerId, setCurrentSellerId] = useState(null);
  const [showCategory, setShowCategory] = useState("verified"); // 'verified', 'pending', 'deactivated'
  const [searchTerm, setSearchTerm] = useState("");
  const [filterVendorType, setFilterVendorType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'
  const [showFilters, setShowFilters] = useState(false);

  //   Get Sellers with redux
  const dispatch = useDispatch();
  const history = useHistory();
  const { sellers, loading } = useSelector((state) => state.sellers);
  const user = getUserFromLocalStorage();

  // Derive all vendor types present in the data for the dropdown
  const vendorTypes = useMemo(() => {
    const types = sellers.map((s) => s.operationMode).filter(Boolean);
    return [...new Set(types)].sort();
  }, [sellers]);

  // Core search + filter + sort pipeline
  const applyFilters = (list) => {
    let result = list.filter((seller) =>
      [seller.businessName, seller.email, seller.typeOfGoods, seller.operationMode, seller.businessPhone]
        .join(" ").toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filterVendorType)
      result = result.filter((s) => s.operationMode === filterVendorType);
    if (filterDateFrom)
      result = result.filter((s) => new Date(s.createdAt) >= new Date(filterDateFrom));
    if (filterDateTo)
      result = result.filter((s) => new Date(s.createdAt) <= new Date(filterDateTo + "T23:59:59"));
    result = [...result].sort((a, b) => {
      let valA = a[sortField] ?? "";
      let valB = b[sortField] ?? "";
      if (sortField === "createdAt") { valA = new Date(valA); valB = new Date(valB); }
      else { valA = String(valA).toLowerCase(); valB = String(valB).toLowerCase(); }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  };

  // Verified Sellers
  const verifiedSellers = useMemo(
    () => applyFilters(sellers.filter((s) => s.sellerVerification === "VERIFIED" && s.isActive)),
    [sellers, searchTerm, filterVendorType, filterDateFrom, filterDateTo, sortField, sortDir]
  );

  // Pending Sellers
  const pendingVerificationSellers = useMemo(
    () => applyFilters(sellers.filter((s) => s.sellerVerification === "PENDING" && s.isActive)),
    [sellers, searchTerm, filterVendorType, filterDateFrom, filterDateTo, sortField, sortDir]
  );

  // Deactivated Sellers
  const deactivatedSellers = useMemo(
    () => applyFilters(sellers.filter((s) => !s.isActive)),
    [sellers, searchTerm, filterVendorType, filterDateFrom, filterDateTo, sortField, sortDir]
  );

  const activeFiltersCount = [filterVendorType, filterDateFrom, filterDateTo].filter(Boolean).length;

  const clearAllFilters = () => {
    setFilterVendorType("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSortField("createdAt");
    setSortDir("desc");
    setSearchTerm("");
  };

  useEffect(() => {
    dispatch(getSellers(user?.result._id));
  }, []);

  // Deep link from the navbar bell / sidebar chip: /admin/sellers?tab=pending
  // opens that tab directly (any of verified | pending | deactivated).
  const location = useLocation();
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("tab");
    if (requested && ["verified", "pending", "deactivated"].includes(requested)) {
      setShowCategory(requested);
    }
  }, [location.search]);

  // Live refresh: whenever the server announces a change to the verification
  // queue (a registration, a verification by a colleague), reload the list so
  // the tabs and the KPI cards move without a manual reload.
  const sellerQueue = useSellerReviewQueue();
  const lastQueueSeq = sellerQueue.lastEvent?.seq || 0;
  // `staffId` rather than `user`: getUserFromLocalStorage() builds a NEW object
  // on every render, so depending on it would refetch in a loop.
  const staffId = user?.result?._id;
  useEffect(() => {
    if (!lastQueueSeq) return;
    dispatch(getSellers(staffId));
  }, [lastQueueSeq, dispatch, staffId]);

  const handleDelete = (sellerId) => {
    dispatch(deleteSeller(sellerId, user?.result?._id));
    setShowDeleteModal(false);
  };
  const handleSoftDelete = (sellerId) => {
    dispatch(softDeleteSeller(sellerId, user?.result?._id));
    setShowSoftDeleteModal(false);
  };

  const handleActivateVendor = (sellerId) => {
    dispatch(reactivateSeller(sellerId, user?.result?._id));
    setShowActivateModal(false);
  };
  const tabs = [
    { key: "verified",    label: "Verified",     icon: <VerifiedUserIcon style={{ fontSize: 15 }} />, accent: "#117a44" },
    { key: "pending",     label: "Pending",       icon: <TimelapseIcon   style={{ fontSize: 15 }} />, accent: "#c8880a" },
    { key: "deactivated", label: "Deactivated",   icon: <BlockIcon        style={{ fontSize: 15 }} />, accent: "#6c757d" },
  ];

  const activeTab = tabs.find((t) => t.key === showCategory);

  const getInitials = (name = "") =>
    name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  const avatarColors = ["#117a44", "#0d6efd", "#c8880a", "#6f42c1", "#0dcaf0", "#dc3545"];
  const getAvatarColor = (name = "") => avatarColors[name.charCodeAt(0) % avatarColors.length];

  const currentSellers =
    showCategory === "verified"    ? verifiedSellers
    : showCategory === "pending"   ? pendingVerificationSellers
    : deactivatedSellers;

  const emptyMessages = {
    verified:    "No verified sellers found.",
    pending:     "No sellers pending verification.",
    deactivated: "No deactivated sellers.",
  };

  const VendorTypeTag = ({ type }) => {
    const palette = {
      RESTAURANT:  { bg: "#e8f5e9", color: "#2e7d32", dot: "#2e7d32" },
      RETAILER:    { bg: "#e3f2fd", color: "#1565c0", dot: "#1565c0" },
      HEALTHCARE:  { bg: "#fce4ec", color: "#c62828", dot: "#c62828" },
      WHOLESALER:  { bg: "#fff3e0", color: "#e65100", dot: "#e65100" },
    };
    const s = palette[type?.toUpperCase()] || { bg: "#f0f0f0", color: "#555", dot: "#aaa" };
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: s.bg, color: s.color,
        padding: "3px 9px", borderRadius: 20,
        fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.03em",
      }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
        {type || "—"}
      </span>
    );
  };

  const StatusBadge = ({ status, isActive }) => {
    if (!isActive) return (
      <span style={{ background: "#f0f0f0", color: "#6c757d", padding: "3px 10px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 700 }}>
        DEACTIVATED
      </span>
    );
    const map = {
      VERIFIED: { bg: "#e8f5e9", color: "#1b5e20" },
      PENDING:  { bg: "#fff8e1", color: "#795548" },
    };
    const s = map[status] || { bg: "#f0f0f0", color: "#555" };
    return (
      <span style={{ background: s.bg, color: s.color, padding: "3px 10px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 700 }}>
        {status}
      </span>
    );
  };

  return (
    <>
      {loading && (
        <Spinner animation="grow" variant="success"
          style={{ position: "fixed", top: "50%", left: "50%", zIndex: 9999 }}
        />
      )}

      {/* ── Modals (kept outside table to avoid re-render per row) ── */}
      <Modal show={showSoftDeleteModal} onHide={() => setShowSoftDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #dc3545" }}>
          <Modal.Title>
            <BlockIcon style={{ color: "#dc3545", fontSize: 20, marginRight: 8 }} />
            Confirm Vendor Ban
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.9rem" }}>Are you sure you want to ban this vendor?</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSoftDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => handleSoftDelete(currentSellerId)}>Ban Vendor</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showActivateModal} onHide={() => setShowActivateModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #117a44" }}>
          <Modal.Title>
            <VerifiedUserIcon style={{ color: "#117a44", fontSize: 20, marginRight: 8 }} />
            Confirm Vendor Activation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.9rem" }}>Are you sure you want to reactivate this vendor?</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowActivateModal(false)} style={{ minWidth: 120 }}>Cancel</Button>
          <Button variant="success" onClick={() => handleActivateVendor(currentSellerId)} style={{ minWidth: 140 }}>Activate Vendor</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #dc3545" }}>
          <Modal.Title>
            <i className="fas fa-trash" style={{ color: "#dc3545", marginRight: 8 }} />
            Confirm Permanent Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.9rem" }}>This action is <strong>irreversible</strong>. Are you sure you want to permanently delete this vendor?</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => handleDelete(currentSellerId)}>Delete Permanently</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .pbi-page { background: #f4f6f9; min-height: 100vh; padding: 24px 20px; font-family: "Segoe UI", system-ui, sans-serif; }

        /* KPI Cards */
        .pbi-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        .pbi-kpi-card {
          background: #fff;
          border-radius: 10px;
          padding: 18px 20px;
          box-shadow: 0 1px 6px rgba(0,0,0,0.07);
          border-top: 4px solid transparent;
          position: relative;
          overflow: hidden;
        }
        .pbi-kpi-card::after {
          content: "";
          position: absolute; right: -14px; top: -14px;
          width: 70px; height: 70px; border-radius: 50%;
          opacity: 0.07;
        }
        .pbi-kpi-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; margin-bottom: 6px; }
        .pbi-kpi-value { font-size: 2rem; font-weight: 800; line-height: 1; margin-bottom: 4px; }
        .pbi-kpi-sub { font-size: 0.75rem; color: #8a9ab0; }
        .pbi-kpi-icon { position: absolute; right: 18px; top: 50%; transform: translateY(-50%); opacity: 0.12; }

        /* Panel */
        .pbi-panel { background: #fff; border-radius: 10px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; }
        .pbi-panel-header {
          background: linear-gradient(135deg, #1a2a3a 0%, #243447 100%);
          padding: 18px 24px 0;
        }
        .pbi-panel-title { color: #fff; font-size: 1.05rem; font-weight: 700; margin: 0 0 14px; display: flex; align-items: center; gap: 8px; }
        .pbi-panel-title svg { color: #5bc5f5; }

        /* Tabs */
        .pbi-tabs { display: flex; gap: 4px; }
        .pbi-tab {
          padding: 9px 18px; border-radius: 8px 8px 0 0;
          font-size: 0.8rem; font-weight: 600; border: none; cursor: pointer;
          display: flex; align-items: center; gap: 6px;
          transition: background 0.15s, color 0.15s;
          background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.55);
        }
        .pbi-tab:hover { background: rgba(255,255,255,0.13); color: rgba(255,255,255,0.85); }
        .pbi-tab.active { background: #fff; color: #1a2a3a; }
        .pbi-tab-badge {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 20px; height: 20px; border-radius: 10px; padding: 0 6px;
          font-size: 0.7rem; font-weight: 800;
        }

        /* Toolbar */
        .pbi-toolbar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 20px; border-bottom: 1px solid #f0f2f5;
          background: #fafbfc;
        }
        .pbi-search-wrap { position: relative; width: 300px; }
        .pbi-search-wrap .pbi-search-icon {
          position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
          color: #9aacbf; font-size: 13px; pointer-events: none;
        }
        .pbi-search-wrap input {
          width: 100%; padding: 8px 34px 8px 35px;
          border: 1.5px solid #e4e8ef; border-radius: 8px;
          font-size: 0.83rem; outline: none; transition: border-color 0.2s;
          background: #fff;
        }
        .pbi-search-wrap input:focus { border-color: #5bc5f5; }
        .pbi-search-clear {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          background: none; border: none; color: #9aacbf; cursor: pointer; padding: 2px; font-size: 12px;
        }
        .pbi-result-pill {
          display: flex; align-items: center; gap: 7px;
          background: #f0f2f5; color: #495057;
          padding: 6px 14px; border-radius: 20px;
          font-size: 0.78rem; font-weight: 600; border: 1px solid #e4e8ef;
        }
        .pbi-result-pill .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

        /* Table */
        .pbi-table-scroll { overflow-x: auto; }
        table.pbi-table { width: 100%; border-collapse: collapse; }
        table.pbi-table thead tr { background: #f4f6f9; }
        table.pbi-table thead th {
          padding: 10px 14px; font-size: 0.68rem; font-weight: 700;
          letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0;
          border-bottom: 2px solid #e9ecef; white-space: nowrap;
          border-top: none;
        }
        table.pbi-table thead th:first-child { padding-left: 20px; }
        table.pbi-table tbody tr {
          border-left: 3px solid transparent;
          transition: background 0.12s, border-color 0.12s;
        }
        table.pbi-table tbody tr:hover { background: #f7fbff; border-left-color: var(--row-accent, #5bc5f5); }
        table.pbi-table tbody td {
          padding: 11px 14px; font-size: 0.82rem; color: #2d3748;
          border-bottom: 1px solid #f0f2f5; vertical-align: middle;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          max-width: 180px;
        }
        table.pbi-table tbody td:first-child { padding-left: 20px; }
        table.pbi-table tbody tr:last-child td { border-bottom: none; }

        /* Avatar */
        .pbi-avatar {
          width: 32px; height: 32px; border-radius: 8px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 0.7rem; font-weight: 800; color: #fff;
          flex-shrink: 0; margin-right: 8px;
        }
        .pbi-name-cell { display: flex; align-items: center; }
        .pbi-name-text { font-weight: 600; color: #1a2a3a; }
        .pbi-name-sub  { font-size: 0.7rem; color: #9aacbf; }

        /* Action buttons */
        .pbi-actions { display: flex; gap: 5px; }
        .pbi-btn {
          width: 30px; height: 30px; border-radius: 7px; border: 1.5px solid;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 12px; cursor: pointer; transition: background 0.15s, color 0.15s;
          background: transparent;
        }
        .pbi-btn-view  { border-color: #c8d6e5; color: #6b7c93; }
        .pbi-btn-view:hover  { background: #e8f4fd; border-color: #5bc5f5; color: #1a2a3a; }
        .pbi-btn-edit  { border-color: #c8d6e5; color: #6b7c93; }
        .pbi-btn-edit:hover  { background: #fff3cd; border-color: #ffc107; color: #856404; }
        .pbi-btn-ban   { border-color: #f5c2c7; color: #dc3545; }
        .pbi-btn-ban:hover   { background: #f8d7da; }
        .pbi-btn-del   { border-color: #f5c2c7; color: #dc3545; }
        .pbi-btn-del:hover   { background: #f8d7da; }
        .pbi-btn-activate { border-color: #badbcc; color: #117a44; }
        .pbi-btn-activate:hover { background: #d1e7dd; }
        .pbi-btn:disabled { opacity: 0.35; cursor: not-allowed; }

        /* Empty state */
        .pbi-empty { text-align: center; padding: 52px 20px; }
        .pbi-empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.25; }
        .pbi-empty-msg { font-size: 0.88rem; color: #9aacbf; font-style: italic; }

        /* Filter bar */
        .pbi-filter-bar {
          padding: 10px 20px 0;
          background: #fafbfc;
          border-bottom: 1px solid #f0f2f5;
        }
        .pbi-filter-row {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
          padding-bottom: 10px;
        }
        .pbi-filter-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.07em;
          text-transform: uppercase; color: #8a9ab0; white-space: nowrap;
        }
        .pbi-filter-select, .pbi-filter-date {
          padding: 6px 10px; border: 1.5px solid #e4e8ef; border-radius: 8px;
          font-size: 0.8rem; background: #fff; color: #2d3748;
          outline: none; transition: border-color 0.2s; cursor: pointer;
          height: 34px;
        }
        .pbi-filter-select:focus, .pbi-filter-date:focus { border-color: #5bc5f5; }
        .pbi-filter-select.active, .pbi-filter-date.active {
          border-color: #5bc5f5; background: #f0f9ff; color: #1565c0; font-weight: 600;
        }
        .pbi-sort-btn {
          height: 34px; padding: 0 12px; border: 1.5px solid #e4e8ef; border-radius: 8px;
          background: #fff; color: #6b7c93; font-size: 0.8rem; font-weight: 600;
          cursor: pointer; display: flex; align-items: center; gap: 5px;
          transition: background 0.15s, border-color 0.15s;
        }
        .pbi-sort-btn:hover { background: #f0f9ff; border-color: #5bc5f5; color: #1565c0; }
        .pbi-sort-btn.active { background: #e8f4fd; border-color: #5bc5f5; color: #1565c0; }
        .pbi-filter-divider { width: 1px; height: 22px; background: #e4e8ef; margin: 0 4px; }
        .pbi-filter-toggle {
          height: 34px; padding: 0 14px;
          border: 1.5px solid #e4e8ef; border-radius: 8px;
          background: #fff; font-size: 0.8rem; font-weight: 600; color: #6b7c93;
          cursor: pointer; display: flex; align-items: center; gap: 6px;
          transition: background 0.15s, border-color 0.15s;
        }
        .pbi-filter-toggle:hover { background: #f0f9ff; border-color: #5bc5f5; color: #1565c0; }
        .pbi-filter-toggle.has-filters { border-color: #5bc5f5; background: #e8f4fd; color: #1565c0; }
        .pbi-filter-badge {
          background: #5bc5f5; color: #fff;
          width: 18px; height: 18px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 0.65rem; font-weight: 800;
        }
        .pbi-chips-row {
          display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
          padding-bottom: 10px;
        }
        .pbi-chip {
          display: inline-flex; align-items: center; gap: 5px;
          background: #e8f4fd; color: #1565c0; border: 1px solid #b3d9f7;
          padding: 3px 10px 3px 10px; border-radius: 20px;
          font-size: 0.74rem; font-weight: 600;
        }
        .pbi-chip-remove {
          background: none; border: none; cursor: pointer; padding: 0;
          color: #6aacdb; font-size: 11px; display: flex; align-items: center;
          line-height: 1; margin-left: 2px;
        }
        .pbi-chip-remove:hover { color: #dc3545; }
        .pbi-clear-all {
          background: none; border: none; cursor: pointer;
          font-size: 0.74rem; font-weight: 600; color: #dc3545;
          padding: 3px 8px; border-radius: 6px;
          transition: background 0.15s;
        }
        .pbi-clear-all:hover { background: #f8d7da; }

        /* Clickable sort headers */
        table.pbi-table thead th.sortable { cursor: pointer; user-select: none; }
        table.pbi-table thead th.sortable:hover { color: #5bc5f5; }
        table.pbi-table thead th.sort-active { color: #1565c0; }
        .sort-arrow { margin-left: 4px; font-size: 0.65rem; }
      `}</style>

      <div className="pbi-page">
        {/* ── KPI Cards ── */}
        <div className="pbi-kpi-grid">
          <div className="pbi-kpi-card" style={{ borderTopColor: "#117a44" }}>
            <div className="pbi-kpi-icon"><VerifiedUserIcon style={{ fontSize: 56, color: "#117a44" }} /></div>
            <div className="pbi-kpi-label">Verified Sellers</div>
            <div className="pbi-kpi-value" style={{ color: "#117a44" }}>{verifiedSellers.length}</div>
            <div className="pbi-kpi-sub">Active &amp; verified vendors</div>
          </div>
          <div className="pbi-kpi-card" style={{ borderTopColor: "#c8880a" }}>
            <div className="pbi-kpi-icon"><TimelapseIcon style={{ fontSize: 56, color: "#c8880a" }} /></div>
            <div className="pbi-kpi-label">Pending Verification</div>
            <div className="pbi-kpi-value" style={{ color: "#c8880a" }}>{pendingVerificationSellers.length}</div>
            <div className="pbi-kpi-sub">Awaiting review</div>
          </div>
          <div className="pbi-kpi-card" style={{ borderTopColor: "#6c757d" }}>
            <div className="pbi-kpi-icon"><BlockIcon style={{ fontSize: 56, color: "#6c757d" }} /></div>
            <div className="pbi-kpi-label">Deactivated</div>
            <div className="pbi-kpi-value" style={{ color: "#6c757d" }}>{deactivatedSellers.length}</div>
            <div className="pbi-kpi-sub">Suspended accounts</div>
          </div>
          <div className="pbi-kpi-card" style={{ borderTopColor: "#0d6efd" }}>
            <div className="pbi-kpi-icon"><StorefrontIcon style={{ fontSize: 56, color: "#0d6efd" }} /></div>
            <div className="pbi-kpi-label">Total Sellers</div>
            <div className="pbi-kpi-value" style={{ color: "#0d6efd" }}>{sellers.length}</div>
            <div className="pbi-kpi-sub">All registered vendors</div>
          </div>
        </div>

        {/* ── Main Panel ── */}
        <div className="pbi-panel">
          <div className="pbi-panel-header">
            <div className="pbi-panel-title">
              <StorefrontIcon style={{ fontSize: 20 }} />
              Seller Directory
            </div>
            <div className="pbi-tabs">
              {tabs.map((tab) => {
                const count =
                  tab.key === "verified"    ? verifiedSellers.length
                  : tab.key === "pending"   ? pendingVerificationSellers.length
                  : deactivatedSellers.length;
                return (
                  <button
                    key={tab.key}
                    className={`pbi-tab${showCategory === tab.key ? " active" : ""}`}
                    onClick={() => { setShowCategory(tab.key); setSearchTerm(""); }}
                  >
                    {tab.icon} {tab.label}
                    <span className="pbi-tab-badge"
                      style={{
                        background: showCategory === tab.key ? tab.accent : "rgba(255,255,255,0.15)",
                        color: showCategory === tab.key ? "#fff" : "rgba(255,255,255,0.6)",
                      }}
                    >{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Toolbar + Filter Bar ── */}
          <div className="pbi-filter-bar">

            {/* Row 1: search + filter toggle + result pill */}
            <div className="pbi-filter-row">
              <div className="pbi-search-wrap">
                <i className="fas fa-search pbi-search-icon" />
                <input
                  type="text"
                  placeholder={`Search ${showCategory} sellers…`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="pbi-search-clear" onClick={() => setSearchTerm("")}>
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>

              <div className="pbi-filter-divider" />

              {/* Filter toggle button */}
              <button
                className={`pbi-filter-toggle${activeFiltersCount > 0 ? " has-filters" : ""}`}
                onClick={() => setShowFilters((v) => !v)}
              >
                <i className="fas fa-sliders-h" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="pbi-filter-badge">{activeFiltersCount}</span>
                )}
                <i className={`fas fa-chevron-${showFilters ? "up" : "down"}`} style={{ fontSize: 10, marginLeft: 2 }} />
              </button>

              <div className="pbi-filter-divider" />

              {/* Sort field */}
              <span className="pbi-filter-label">Sort</span>
              <AppSelect
                className={`pbi-filter-select${sortField !== "createdAt" ? " active" : ""}`}
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                style={{ minWidth: 150 }}
              >
                <option value="createdAt">Date Joined</option>
                <option value="businessName">Business Name</option>
                <option value="operationMode">Vendor Type</option>
                <option value="typeOfGoods">Type of Goods</option>
              </AppSelect>

              {/* Sort direction toggle */}
              <button
                className={`pbi-sort-btn${sortDir === "asc" ? " active" : ""}`}
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                title={sortDir === "asc" ? "Ascending — click to switch" : "Descending — click to switch"}
              >
                <i className={`fas fa-sort-amount-${sortDir === "asc" ? "up" : "down"}`} />
                {sortDir === "asc" ? "A → Z" : "Z → A"}
              </button>

              {/* Spacer + result pill */}
              <div style={{ marginLeft: "auto" }}>
                <div className="pbi-result-pill">
                  <span className="dot" style={{ background: activeTab?.accent }} />
                  {currentSellers.length} {showCategory} seller{currentSellers.length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>

            {/* Row 2: expanded filter controls */}
            {showFilters && (
              <div className="pbi-filter-row" style={{ paddingTop: 0, borderTop: "1px dashed #e4e8ef", marginTop: 2, paddingBottom: 12 }}>
                <span className="pbi-filter-label">Vendor Type</span>
                <AppSelect
                  className={`pbi-filter-select${filterVendorType ? " active" : ""}`}
                  value={filterVendorType}
                  onChange={(e) => setFilterVendorType(e.target.value)}
                  style={{ minWidth: 160 }}
                >
                  <option value="">All Types</option>
                  {vendorTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </AppSelect>

                <div className="pbi-filter-divider" />

                <span className="pbi-filter-label">Joined from</span>
                <input
                  type="date"
                  className={`pbi-filter-date${filterDateFrom ? " active" : ""}`}
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                />

                <span className="pbi-filter-label">to</span>
                <input
                  type="date"
                  className={`pbi-filter-date${filterDateTo ? " active" : ""}`}
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                />

                {activeFiltersCount > 0 && (
                  <button className="pbi-clear-all" onClick={clearAllFilters}>
                    <i className="fas fa-times-circle" style={{ marginRight: 4 }} />
                    Clear all
                  </button>
                )}
              </div>
            )}

            {/* Row 3: active filter chips */}
            {activeFiltersCount > 0 && (
              <div className="pbi-chips-row">
                <span className="pbi-filter-label" style={{ marginRight: 2 }}>Active:</span>
                {filterVendorType && (
                  <span className="pbi-chip">
                    <i className="fas fa-store" style={{ fontSize: 10 }} />
                    {filterVendorType}
                    <button className="pbi-chip-remove" onClick={() => setFilterVendorType("")}>
                      <i className="fas fa-times" />
                    </button>
                  </span>
                )}
                {filterDateFrom && (
                  <span className="pbi-chip">
                    <i className="fas fa-calendar" style={{ fontSize: 10 }} />
                    From {moment(filterDateFrom).format("DD MMM YYYY")}
                    <button className="pbi-chip-remove" onClick={() => setFilterDateFrom("")}>
                      <i className="fas fa-times" />
                    </button>
                  </span>
                )}
                {filterDateTo && (
                  <span className="pbi-chip">
                    <i className="fas fa-calendar-check" style={{ fontSize: 10 }} />
                    To {moment(filterDateTo).format("DD MMM YYYY")}
                    <button className="pbi-chip-remove" onClick={() => setFilterDateTo("")}>
                      <i className="fas fa-times" />
                    </button>
                  </span>
                )}
                <button className="pbi-clear-all" onClick={clearAllFilters}>
                  <i className="fas fa-times-circle" style={{ marginRight: 4 }} />
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="pbi-table-scroll">
            <table className="pbi-table" style={{ "--row-accent": activeTab?.accent }}>
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  {[
                    { label: "Seller",        field: "businessName" },
                    { label: "Vendor Type",   field: "operationMode" },
                    { label: "Email",         field: "email" },
                    { label: "Phone",         field: null },
                    { label: "Type of Goods", field: "typeOfGoods" },
                    { label: "Joined",        field: "createdAt" },
                    { label: "Status",        field: null },
                    { label: "Actions",       field: null },
                  ].map(({ label, field }) => (
                    <th
                      key={label}
                      className={`${field ? "sortable" : ""}${sortField === field ? " sort-active" : ""}`}
                      onClick={() => {
                        if (!field) return;
                        if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
                        else { setSortField(field); setSortDir("asc"); }
                      }}
                    >
                      {label}
                      {field && (
                        <span className="sort-arrow">
                          {sortField === field
                            ? (sortDir === "asc" ? " ▲" : " ▼")
                            : " ⇅"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentSellers.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="pbi-empty">
                        <div className="pbi-empty-icon">
                          {showCategory === "verified" && <VerifiedUserIcon style={{ fontSize: 42 }} />}
                          {showCategory === "pending"  && <TimelapseIcon   style={{ fontSize: 42 }} />}
                          {showCategory === "deactivated" && <BlockIcon   style={{ fontSize: 42 }} />}
                        </div>
                        <div className="pbi-empty-msg">{emptyMessages[showCategory]}</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentSellers.map((seller, index) => (
                    <tr key={seller._id}>
                      <td style={{ color: "#9aacbf", fontWeight: 700, fontSize: "0.75rem" }}>{index + 1}</td>
                      <td>
                        <div className="pbi-name-cell">
                          <span className="pbi-avatar" style={{ background: getAvatarColor(seller.businessName) }}>
                            {getInitials(seller.businessName)}
                          </span>
                          <div>
                            <div className="pbi-name-text">{seller.businessName}</div>
                            <div className="pbi-name-sub">
                              {seller.sellerVerification === "VERIFIED"
                                ? <><VerifiedUserIcon style={{ fontSize: 10, color: "#117a44", marginRight: 3 }} />Verified</>
                                : <><TimelapseIcon   style={{ fontSize: 10, color: "#c8880a", marginRight: 3 }} />Pending</>
                              }
                            </div>
                          </div>
                        </div>
                      </td>
                      <td><VendorTypeTag type={seller.operationMode} /></td>
                      <td title={seller.email} style={{ color: "#4a5568" }}>{seller.email}</td>
                      <td style={{ fontFamily: "monospace", letterSpacing: "0.02em" }}>{seller.businessPhone}</td>
                      <td title={seller.typeOfGoods} style={{ color: "#4a5568" }}>{seller.typeOfGoods || <span style={{ color: "#c0c8d4" }}>—</span>}</td>
                      <td style={{ color: "#6b7c93", fontSize: "0.78rem" }}>
                        <div style={{ fontWeight: 600 }}>{moment(seller.createdAt).format("DD MMM YYYY")}</div>
                        <div style={{ fontSize: "0.68rem", color: "#9aacbf" }}>{moment(seller.createdAt).fromNow()}</div>
                      </td>
                      <td><StatusBadge status={seller.sellerVerification} isActive={seller.isActive} /></td>
                      <td>
                        <div className="pbi-actions">
                          {/* View */}
                          <OverlayTrigger placement="top" overlay={<Tooltip>View Details</Tooltip>}>
                            <button className="pbi-btn pbi-btn-view" onClick={() => {
                              dispatch(getSellerDetail(seller._id));
                              history.push(`/admin/sellerDetail/${seller._id}`);
                            }}>
                              <i className="fas fa-eye" />
                            </button>
                          </OverlayTrigger>

                          {/* Edit — verified & pending only */}
                          {seller.isActive && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Manage Vendor</Tooltip>}>
                              <button className="pbi-btn pbi-btn-edit" onClick={() => {
                                dispatch(getSellerDetail(seller._id));
                                history.push(`/admin/sellerEdit/${seller._id}`);
                              }}>
                                <i className="fas fa-edit" />
                              </button>
                            </OverlayTrigger>
                          )}

                          {/* Ban — pending only */}
                          {showCategory === "pending" && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Ban Vendor</Tooltip>}>
                              <button className="pbi-btn pbi-btn-ban" onClick={() => {
                                setCurrentSellerId(seller._id);
                                setShowSoftDeleteModal(true);
                              }}>
                                <i className="fas fa-ban" />
                              </button>
                            </OverlayTrigger>
                          )}

                          {/* Activate — deactivated only */}
                          {showCategory === "deactivated" && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Activate Vendor</Tooltip>}>
                              <button className="pbi-btn pbi-btn-activate" onClick={() => {
                                dispatch(getSellerDetail(seller._id));
                                setCurrentSellerId(seller._id);
                                setShowActivateModal(true);
                              }}>
                                <i className="fas fa-check-circle" />
                              </button>
                            </OverlayTrigger>
                          )}

                          {/* Delete — deactivated only */}
                          {showCategory === "deactivated" && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Delete Permanently</Tooltip>}>
                              <button className="pbi-btn pbi-btn-del" onClick={() => {
                                setCurrentSellerId(seller._id);
                                setShowDeleteModal(true);
                              }}>
                                <i className="fas fa-trash" />
                              </button>
                            </OverlayTrigger>
                          )}

                          {/* Delete (disabled) — verified only */}
                          {showCategory === "verified" && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Cannot delete verified seller</Tooltip>}>
                              <span>
                                <button className="pbi-btn pbi-btn-del" disabled>
                                  <i className="fas fa-trash" />
                                </button>
                              </span>
                            </OverlayTrigger>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sellers;
